<template>
  <div
    v-if="stepData.typeOfParty === 'F'"
    class="form-section"
  >
    <div v-if="twoAdults">
      <question-base-wrapper
        :id="`${adultId}`"
        v-bind="baseWrapperProps"
        :class="additionalClasses"
        :title="getIterableTitle(2)"
      >
        <validation-provider
          :vid="`${stepId}_${adultId}`"
          :name="`${adultId}`"
          :rules="adultRules"
        >
          <qz-date-select
            v-model="travellers.adults[0].dateOfBirth"
            :form-observer="formObserver"
            :data-test="snakeCase(questionFields.field)"
            :vid="`${stepId}_${adultId}`"
            :year-options="adultYearsOptions"
            :date-rule="adultRules"
          />
        </validation-provider>
      </question-base-wrapper>
    </div>

    <div v-if="amountOfChildren">
      <div
        v-for="item in amountOfChildren"
        :key="item"
      >
        <question-base-wrapper
          :id="`${childId}_${item}`"
          v-bind="baseWrapperProps"
          :class="additionalClasses"
          :title="twoAdults ? getIterableTitle(item + 2) : getIterableTitle(item)"
        >
          <validation-provider
            :vid="`${stepId}_${childId}_${item}`"
            :name="`${childId}_${item}`"
            :rules="dateValidationRules"
          >
            <qz-date-select
              v-model="travellers.children[item - 1].dateOfBirth"
              :form-observer="formObserver"
              :data-test="snakeCase(questionFields.field)"
              :vid="`${stepId}_${childId}_${item}`"
              :year-options="getYearsOptions"
              :date-rule="dateValidationRules"
            />
          </validation-provider>
        </question-base-wrapper>
      </div>
    </div>
  </div>
</template>

<script>
import BaseDate from './BaseDate';
import { getRangeOfYears, UtcDate } from '@/helpers/date';
import { toWordsOrdinal } from 'number-to-words';

export default {
  name: 'question-date-of-birth-family',
  extends: BaseDate,
  props: {
    isVerticalSplitted: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      childId: 'date-of-birth-child',
      adultId: 'family-adult',
      travellers: {
        adults: [],
        children: [],
      },
      adultRules: {
        minAge: this.constants.INSURER_MIN_AGE,
        maxAge: this.constants.INSURER_MAX_AGE,
        adultDateOfBirth: {
          person: 'The second traveller',
        },
      },
    };
  },
  computed: {
    amountOfChildren () {
      return this.stepData.travellingChildren;
    },
    amountOfAdults () {
      return this.stepData.travellingAdults;
    },
    twoAdults () {
      return this.amountOfAdults === 2;
    },
    adultYearsOptions () {
      return getRangeOfYears(this.constants.INSURER_MAX_AGE - this.constants.INSURER_MIN_AGE, new UtcDate().getFullYear() - this.constants.INSURER_MIN_AGE);
    },
    additionalClasses () {
      return {
        vertically: this.isVerticalSplitted,
      };
    },
  },
  watch: {
    travellers: {
      handler (val) {
        this.stepData.dateOfBirthFamily = Object.fromEntries(
          // convert to array then map and fromEntries back to object
          Object.entries(val).map(([key, value]) => {
            const newVal = value.map(({ dateOfBirth }) => {
              if (!dateOfBirth) return;

              return dateOfBirth;
            });
            return [key, newVal];
          }),
        );
      },
      deep: true,
    },
    amountOfChildren (newVal, old) {
      this.updateTravellers(newVal, old, 'children');
    },
    amountOfAdults (newVal, old) {
      this.updateTravellers(newVal, old, 'adults');
    },
  },
  created () {
    if (this.stepData.dateOfBirthFamily) this.initDefaultValue();
  },
  methods: {
    updateTravellers (newVal, old, type) {
      if (old === newVal) return;

      if (old === null) {
        // if type is adults than we already have 1 adult created
        const amount = type === 'adults' ? newVal - 1 : newVal;

        this.addItemsToDob(amount, type);
      } else {
        this.updateDobValue(newVal, old, type);
      }
    },
    updateDobValue (newVal, old, type) {
      const newIsBigger = newVal > old;
      const amountDifference = newIsBigger ? newVal - old : old - newVal;

      if (newIsBigger) {
        this.addItemsToDob(amountDifference, type);
      } else {
        this.travellers[type].splice(-amountDifference);
      }
    },
    initDefaultValue () {
      for (const [key, value] of Object.entries(this.stepData.dateOfBirthFamily)) {
        value.forEach(dateOfBirth => {
          this.travellers[key].push({
            dateOfBirth,
          });
        });
      }
    },
    addItemsToDob (amount, type) {
      for (let i = 0; i < amount; i++) {
        this.travellers[type].push({
          dateOfBirth: '',
        });
      }
    },
    getIterableTitle (n) {
      const modifiedTitle = this.title.split(' ');

      if (this.twoAdults && n === 2) {
        modifiedTitle.splice(1, 1, 'your partner\'s');
      } else if (this.twoAdults && n !== 2) {
        modifiedTitle.splice(2, 0, `${toWordsOrdinal(n - 2)} child's`);
      } else {
        modifiedTitle.splice(2, 0, `${toWordsOrdinal(n)} child's`);
      }

      return modifiedTitle.join(' ');
    },
  },
};
</script>

<style lang="scss" scoped src="@/assets/scss/shared/vertical-row.scss"/>
